import { create } from 'zustand';
import type { PrintArea, CanvasSize, BackgroundConfig } from '../types/canvas';
import type { Template } from '../types/template';
import type { ToolType } from '../types/tools';

interface DesignerState {
  // Product context
  productId: string | null;
  variantId: string | null;
  designId: string | null;

  // Canvas state
  canvasSize: CanvasSize;
  printArea: PrintArea | null;
  zoom: number;

  // Tool state
  currentTool: ToolType;

  // Selection state
  selectedObjectIds: string[];
  activeObject: fabric.Object | null;

  // Template state
  template: Template | null;
  printType: string | null;
  laserOnly: boolean;
  allowedColors: string[] | null;
  background: BackgroundConfig | null;

  // UI state
  isLoading: boolean;
  loadingText: string;
  status: string;

  // Actions
  setProductContext: (productId: string, variantId?: string, designId?: string) => void;
  setCanvasSize: (size: CanvasSize) => void;
  setPrintArea: (area: PrintArea | null) => void;
  setZoom: (zoom: number) => void;
  setTool: (tool: ToolType) => void;
  setSelection: (objectIds: string[], activeObject: fabric.Object | null) => void;
  clearSelection: () => void;
  setTemplate: (template: Template | null) => void;
  setLaserOnly: (laserOnly: boolean, allowedColors?: string[]) => void;
  setBackground: (background: BackgroundConfig | null) => void;
  setLoading: (isLoading: boolean, text?: string) => void;
  setStatus: (status: string) => void;
  setDesignId: (designId: string) => void;
}

export const useDesignerStore = create<DesignerState>((set) => ({
  // Initial state
  productId: null,
  variantId: null,
  designId: null,

  canvasSize: { width: 800, height: 800 },
  printArea: null,
  zoom: 1.0,

  currentTool: 'select',

  selectedObjectIds: [],
  activeObject: null,

  template: null,
  printType: null,
  laserOnly: false,
  allowedColors: null,
  background: null,

  isLoading: false,
  loadingText: '',
  status: '',

  // Actions
  setProductContext: (productId, variantId, designId) =>
    set({ productId, variantId: variantId ?? null, designId: designId ?? null }),

  setCanvasSize: (canvasSize) => set({ canvasSize }),

  setPrintArea: (printArea) => set({ printArea }),

  setZoom: (zoom) => set({ zoom }),

  setTool: (currentTool) => set({ currentTool }),

  setSelection: (selectedObjectIds, activeObject) =>
    set({ selectedObjectIds, activeObject }),

  clearSelection: () => set({ selectedObjectIds: [], activeObject: null }),

  setTemplate: (template) => set({
    template,
    printType: template?.printType || null,
    laserOnly: template?.laserOnly || template?.printType === 'laser',
  }),

  setLaserOnly: (laserOnly, allowedColors) =>
    set({ laserOnly, allowedColors: allowedColors ?? null }),

  setBackground: (background) => set({ background }),

  setLoading: (isLoading, loadingText = '') => set({ isLoading, loadingText }),

  setStatus: (status) => set({ status }),

  setDesignId: (designId) => set({ designId }),
}));

// Selectors for common state access patterns
export const selectProductContext = (state: DesignerState) => ({
  productId: state.productId,
  variantId: state.variantId,
  designId: state.designId,
});

export const selectCanvasState = (state: DesignerState) => ({
  canvasSize: state.canvasSize,
  printArea: state.printArea,
  zoom: state.zoom,
});

export const selectToolState = (state: DesignerState) => ({
  currentTool: state.currentTool,
  laserOnly: state.laserOnly,
  allowedColors: state.allowedColors,
});
